import React from "react";
import { Box, Button, Input } from "@twilio-paste/core";
import { Text } from "@twilio-paste/text";
import { SendIcon } from "@twilio-paste/icons/esm/SendIcon";

import type { TemplateDto } from "../../../dto/template";
import { useTemplateOnlyComposer } from "../../../hooks/useTemplateOnlyComposer";
import type { InputBoxTypes } from "@twilio-paste/input-box";

type Props = {
  templates: TemplateDto[];
  onSend: (
    template: TemplateDto,
    variables: Record<string, string>
  ) => Promise<void> | void;
  disabled?: boolean;
};

export const TemplateOnlyComposer: React.FC<Props> = ({
  templates,
  onSend,
  disabled = false,
}) => {
  const {
    selectedId,
    setSelectedId,
    selected,
    vars,
    setVar,
    inputs,
    hasMissingVars,
    resetVars,
    previewNodes,
  } = useTemplateOnlyComposer(templates);

  const buildVariablesPayload = (tpl: TemplateDto): Record<string, string> => {
    const payload: Record<string, string> = {};
    const list = tpl.variables ?? [];
    for (const v of list) {
      payload[v.key] = (vars[v.key] ?? "").toString();
    }
    return payload;
  };

  const handleSendSelected = () => {
    if (!selected) return;
    if (disabled) return;
    if (selected.variables?.length && hasMissingVars) return;
    const variablesPayload = buildVariablesPayload(selected);
    onSend(selected, variablesPayload);
    resetVars();
  };

  return (
    <Box
      borderTopStyle="solid"
      borderTopWidth="borderWidth10"
      borderTopColor="colorBorderWeak"
      backgroundColor="colorBackgroundBody"
      padding="space40"
      style={{
        minHeight: "unset",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Text as="div" fontSize="fontSize20" fontWeight="fontWeightSemibold">
          Seleziona un template
        </Text>
      </Box>

      {/* Lista */}
      <Box
        marginTop="space30"
        borderStyle="solid"
        borderWidth="borderWidth10"
        borderColor="colorBorderWeak"
        borderRadius="borderRadius20"
        display="flex"
        flexDirection="column"
        maxHeight="200px"
        overflowY="auto"
        style={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        {templates.length === 0 ? (
          <Box padding="space40">
            <Text as="div" color="colorTextWeak">
              Nessun template disponibile.
            </Text>
          </Box>
        ) : (
          templates.map((t, idx) => {
            const isSelected = t.id === selectedId;
            const isLast = idx === templates.length - 1;

            const tplHasVars = !!t.variables?.length;

            // - se non ha variabili => invio sempre possibile
            // - se ha variabili => invio possibile SOLO se è selezionato e non mancano variabili
            const canSendThisTemplate =
              !disabled && (!tplHasVars || (isSelected && !hasMissingVars));

            const handleSendThis = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (!canSendThisTemplate) return;

              void onSend(t, buildVariablesPayload(t));
            };

            return (
              <Box
                key={t.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(t.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedId(t.id);
                  }
                }}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding="space40"
                borderBottomStyle={isLast ? "none" : "solid"}
                borderBottomWidth="borderWidth10"
                borderBottomColor="colorBorderWeak"
                style={{
                  cursor: "pointer",
                  background: isSelected
                    ? "rgba(2, 99, 224, 0.08)"
                    : "transparent",
                }}
              >
                <Box minWidth={0} flex="1 1 auto" paddingRight="space40">
                  <Text
                    as="div"
                    color={isSelected ? "colorText" : "colorTextWeak"}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={t.content}
                  >
                    {t.content}
                  </Text>
                </Box>

                <Button
                  variant="link"
                  size="reset"
                  disabled={!canSendThisTemplate}
                  onClick={handleSendThis}
                  aria-label={`Invia template ${t.name}`}
                >
                  <SendIcon decorative />
                </Button>
              </Box>
            );
          })
        )}
      </Box>

      {/* Preview */}
      {selected ? (
        <Box marginTop="space30">
          <Text as="div" color="colorTextWeak">
            Selezionato: <strong>{selected.name}</strong>
          </Text>

          <Box marginTop="space30">
            <Text
              as="p"
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
              }}
            >
              {previewNodes}
            </Text>
          </Box>
        </Box>
      ) : null}

      {/* Variabili */}
      {selected?.variables?.length ? (
        <Box marginTop="space40">
          <Text as="div" fontSize="fontSize20" fontWeight="fontWeightSemibold">
            Compila i campi richiesti:
          </Text>

          <Box
            marginTop="space30"
            display="flex"
            flexDirection="column"
            rowGap="space30"
          >
            {inputs.map((f, index) => (
              <Box key={f.key ?? String(index)}>
                <Text as="label" display="block" marginBottom="space20">
                  <Text as="span" fontWeight="fontWeightSemibold">
                    {f.label}
                  </Text>
                  <Text as="span" color="colorTextWeak">
                    {f.type ? ` (${f.type})` : ""}
                  </Text>
                </Text>

                <Input
                  type={f.inputType as InputBoxTypes}
                  value={f.value}
                  onChange={(e) => setVar(f.key, e.target.value)}
                  placeholder={f.label}
                />
              </Box>
            ))}
          </Box>

          {hasMissingVars ? (
            <Box marginTop="space30">
              <Text as="p" color="colorTextWeak" style={{ margin: 0 }}>
                Compila tutti i campi per poter inviare il template.
              </Text>
            </Box>
          ) : null}

          <Box marginTop="space40" display="flex" justifyContent="flex-end">
            <Button
              variant="primary"
              disabled={disabled || hasMissingVars || !selected}
              onClick={(e) => {
                e.stopPropagation();
                handleSendSelected();
              }}
            >
              Invia template
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
